import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, RefreshCw, ExternalLink, ClipboardList } from "lucide-react";
import { format, formatDistanceToNow, isValid } from "date-fns";
import { Link } from "react-router-dom";

type ReviewRow = {
  id: string;
  title: string;
  kind: "article" | "symptom";
  slug: string | null;
  clinical_grade: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  next_review_at: string | null;
};

const GRADE_STYLES: Record<string, string> = {
  "guideline-backed": "bg-emerald-100 text-emerald-900 border-emerald-200",
  "clinician-reviewed": "bg-sky-100 text-sky-900 border-sky-200",
  editorial: "bg-amber-100 text-amber-900 border-amber-200",
};

function statusFor(row: ReviewRow): {
  label: string;
  tone: "overdue" | "soon" | "ok" | "never";
} {
  if (!row.reviewed_at) return { label: "Never reviewed", tone: "never" };
  if (!row.next_review_at) return { label: "No due date set", tone: "soon" };
  const due = new Date(row.next_review_at);
  if (!isValid(due)) return { label: "No due date set", tone: "soon" };
  const now = Date.now();
  const diff = due.getTime() - now;
  if (diff < 0)
    return {
      label: `Overdue by ${formatDistanceToNow(due)}`,
      tone: "overdue",
    };
  if (diff < 1000 * 60 * 60 * 24 * 30)
    return { label: `Due in ${formatDistanceToNow(due)}`, tone: "soon" };
  return { label: `Due ${format(due, "MMM yyyy")}`, tone: "ok" };
}

const toneClass = {
  overdue: "bg-red-100 text-red-900 border-red-200",
  soon: "bg-amber-100 text-amber-900 border-amber-200",
  ok: "bg-emerald-100 text-emerald-900 border-emerald-200",
  never: "bg-muted text-foreground border-border",
};

export default function ContentReviewAdmin() {
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "due" | "never">("due");

  const load = async () => {
    setLoading(true);
    try {
      const [articlesRes, symptomsRes] = await Promise.all([
        supabase
          .from("articles")
          .select(
            "id, title, slug, clinical_grade, reviewed_at, reviewed_by, next_review_at",
          ),
        supabase
          .from("symptom_library")
          .select(
            "id, title, slug, clinical_grade, reviewed_at, reviewed_by, next_review_at",
          ),
      ]);

      const articles: ReviewRow[] = (articlesRes.data ?? []).map((a: any) => ({
        id: a.id,
        title: a.title,
        slug: a.slug,
        kind: "article",
        clinical_grade: a.clinical_grade,
        reviewed_at: a.reviewed_at,
        reviewed_by: a.reviewed_by,
        next_review_at: a.next_review_at,
      }));
      const symptoms: ReviewRow[] = (symptomsRes.data ?? []).map((s: any) => ({
        id: s.id,
        title: s.title,
        slug: s.slug,
        kind: "symptom",
        clinical_grade: s.clinical_grade,
        reviewed_at: s.reviewed_at,
        reviewed_by: s.reviewed_by,
        next_review_at: s.next_review_at,
      }));

      // Sort: never-reviewed first, then by next_review_at ascending (overdue first)
      const combined = [...articles, ...symptoms].sort((a, b) => {
        if (!a.reviewed_at && b.reviewed_at) return -1;
        if (a.reviewed_at && !b.reviewed_at) return 1;
        const ad = a.next_review_at
          ? new Date(a.next_review_at).getTime()
          : Number.POSITIVE_INFINITY;
        const bd = b.next_review_at
          ? new Date(b.next_review_at).getTime()
          : Number.POSITIVE_INFINITY;
        return ad - bd;
      });

      setRows(combined);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "never") return rows.filter((r) => !r.reviewed_at);
    // "due"
    return rows.filter((r) => {
      if (!r.reviewed_at) return true;
      if (!r.next_review_at) return true;
      return new Date(r.next_review_at).getTime() < Date.now() + 1000 * 60 * 60 * 24 * 60;
    });
  }, [rows, filter]);

  const counts = useMemo(() => {
    let overdue = 0;
    let never = 0;
    for (const r of rows) {
      if (!r.reviewed_at) never++;
      else if (
        r.next_review_at &&
        new Date(r.next_review_at).getTime() < Date.now()
      )
        overdue++;
    }
    return { total: rows.length, overdue, never };
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Content due for review
            </CardTitle>
            <CardDescription>
              Articles and symptom library entries sorted by next review date.
              Never-reviewed items appear first.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-red-100 text-red-900 border-red-200">
              {counts.overdue} overdue
            </Badge>
            <Badge variant="outline">{counts.never} never reviewed</Badge>
            <Badge variant="outline">{counts.total} total</Badge>
            <Button
              size="sm"
              variant="outline"
              onClick={load}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          {(["due", "never", "all"] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              onClick={() => setFilter(f)}
            >
              {f === "due" && "Due / overdue"}
              {f === "never" && "Never reviewed"}
              {f === "all" && "All"}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">
            Nothing to review right now. 🎉
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Last reviewed</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const status = statusFor(r);
                  const gradeClass =
                    GRADE_STYLES[r.clinical_grade ?? "editorial"] ??
                    GRADE_STYLES.editorial;
                  const href =
                    r.kind === "article"
                      ? `/all-the-goss/${r.slug}`
                      : `/symptom-explorer/${r.slug}`;
                  return (
                    <TableRow key={`${r.kind}-${r.id}`}>
                      <TableCell className="font-medium max-w-sm">
                        {r.title}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {r.kind}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={gradeClass}>
                          {r.clinical_grade ?? "editorial"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {r.reviewed_at
                          ? `${format(new Date(r.reviewed_at), "MMM d, yyyy")}${r.reviewed_by ? ` · ${r.reviewed_by}` : ""}`
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={toneClass[status.tone]}
                        >
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {r.slug && (
                          <Link to={href} target="_blank">
                            <Button size="sm" variant="ghost">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
